import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { calculateServiceCost, getServiceName, ProjectService } from 'entities';

import { Card, Collapse, Column, IconButton, Row, Tabs, Tooltip } from 'components/Blocks';
import { ChevronRightIcon, EyeIcon, ShoppingCartIcon } from 'components/Icons';

import { CostHistory } from './CostHistory';
import { SingleProperty } from '../SingleProperty';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { ServiceOutsourcedTag } from 'pages/Projects/components';
import { createExpression } from 'pages/Teams/TeamProfile/Services/calculations';

import { getFirst } from 'utils/array';
import { formatCurrency } from 'utils/text';

import { useToggleV2 } from 'hooks';

import { cn } from 'styles';

interface SingleServiceProps {
  children?: never;
  service: ProjectService;
  enableTaskOpen?: boolean;
  closable?: boolean;
}

export function SingleService({ service, enableTaskOpen, closable = true }: SingleServiceProps) {
  const { t } = useLang();
  const { project, openTask } = useProjectProfile();
  const [showData, vizHandlers] = useToggleV2(true);

  const { costs, outsourceConfig, id } = service;

  const [country, setCountry] = useState(getFirst(project.countries));

  const countryCosts = costs[country];

  const isOutsourced = !!outsourceConfig;

  const task = project.tasks.find(({ serviceConfigId }) => serviceConfigId === id);

  return (
    <>
      <Card flow="column" gap="6">
        <Row gap="2">
          {closable && (
            <IconButton className="text-primary" onClick={vizHandlers.toggle}>
              <ChevronRightIcon className={cn('transition-transform', showData && 'rotate-90')} />
            </IconButton>
          )}

          <Column gap="1">
            <Row gap="2">
              {/* If not assigned there are no tasks! */}
              <p className="font-medium">{getServiceName(id, project.tasks, project.services)}</p>

              {enableTaskOpen && task && (
                <IconButton onClick={() => openTask(task.id)} tooltipContent={t('open')}>
                  <EyeIcon size={14} />
                </IconButton>
              )}
            </Row>

            <Tooltip
              content={`Cost calculation = ${createExpression({
                ...service,
                costVariables: Object.values(service.costs)[0],
                tags: [],
              })}`}
            >
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {formatCurrency(calculateServiceCost(service), project.currency)}
              </span>
            </Tooltip>
          </Column>

          {showData && (
            <Tabs
              responsiveBreakpoint={project.countries.length > 6 ? 'LARGE_SCREEN' : 'PHONE'}
              responsiveOverlay="pop-over"
              overlayProps={{ align: 'end', autoWidth: true }}
              className="ml-auto"
              value={country}
              onTabChange={setCountry}
              disabled={project.countries.length <= 1}
              tabs={project.countries.map((country) => ({
                value: country,
                label: country,
              }))}
            />
          )}
        </Row>

        <Collapse expanded={showData} onlyRenderIfExpanded>
          <Column>
            <div className="grid grid-cols-auto-250 items-end gap-4">
              {countryCosts.map((cost) => (
                <SingleProperty
                  value={formatCurrency(cost.value, cost.currency)}
                  key={cost.id}
                  label={
                    <>
                      {cost.name}
                      <CostHistory service={service} cost={cost} country={country} />
                    </>
                  }
                />
              ))}
            </div>

            {isOutsourced && <hr />}

            {isOutsourced && (
              <Column>
                <Row gap="2">
                  <ShoppingCartIcon />

                  <span className="font-medium italic">{outsourceConfig.providerName}</span>

                  <ServiceOutsourcedTag className="ml-auto" />
                </Row>

                <div className="grid grid-cols-auto-250 items-end gap-4">
                  <SingleProperty
                    value={formatCurrency(
                      outsourceConfig.providerCost.value,
                      outsourceConfig.providerCost.currency,
                    )}
                    label={t('provider-cost')}
                  />

                  <SingleProperty
                    value={formatCurrency(
                      outsourceConfig.clientCost.value,
                      outsourceConfig.clientCost.currency,
                    )}
                    label={t('cost-to-client')}
                  />
                </div>
              </Column>
            )}
          </Column>
        </Collapse>
      </Card>
    </>
  );
}
